import { namespace } from "@formant/core/common/config";
import { emptyArrayToUndefined } from "@formant/core/common/emptyArrayToUndefined";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { getUrlEnvExtension } from "@formant/core/common/utils/customModuleUrlTools";
import { IFilter } from "@formant/core/model/IFilter";
import { ILayoutModuleConfiguration } from "@formant/core/model/ILayoutModuleConfiguration";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { IView } from "@formant/core/model/IView";
import { ModuleType } from "@formant/core/model/ModuleType";

import type { AppStore } from "../store/AppStore";
import type { ObserveStore } from "../store/ObserveStore";
import type { ViewStore } from "../store/ViewStore";
import { findAvailableSpot, getDefaultSize } from "./layoutHelpers";
import { viewConflictMessage } from "./viewConflictMessage";

export const neverAllowedSelfServeModules = [
    "$.agent.buffer",
    "$.agent.health",
    "$.host.geoip",
    "$.ros.node_online",
] as const;

const urlEnvExtension = getUrlEnvExtension(namespace);

const customModuleConfiguration: {
    [k: string]: ILayoutModuleConfiguration | undefined;
} = {
    // "custom-module:string-publisher": {
    //     moduleType: "custom",
    //     customName: "ROS topic publisher",
    //     customVisualizationUrl: `https://formantio.github.io/string-publisher/?auth={auth}&device={device_id}${urlEnvExtension}`
    // },
    // "custom-module:ros_diagnostics": {
    //     moduleType: "custom",
    //     customName: "ROS topic monitor",
    //     customVisualizationUrl: `https://formantio.github.io/ros-diagnostics/?auth={auth}&device={device_id}${urlEnvExtension}`
    // },
    // "custom-module:ros-service-sender": {
    //     moduleType: "custom",
    //     customName: "ROS service caller",
    //     customVisualizationUrl: `https://formantio.github.io/ros-service-call/?auth={auth}&device={device_id}&mode=merged${urlEnvExtension}`
    // },
    "custom-module:terminal": {
        moduleType: "custom",
        customName: "Terminal",
        customVisualizationUrl: `https://formantio.github.io/terminal-module/dist/?auth={auth}&device={device_id}${urlEnvExtension}`,
    },
};

const remove = <T, U extends T>(array: T[] = [], ...items: U[]) =>
    items.reduce((prev, item) => prev.filter(it => it !== item), array);

const isCustomModule = (it: string) => it.startsWith("custom-module:");
const isNotCustomModule = (it: string) => !isCustomModule(it);

export function addModules(
    view: IView,
    info: { [k: string]: ModuleType },
    ...moduleNamesToAdd: string[]
): IView {
    const viewLayout = view.layout as IObserveLayout;
    const customModuleNamesToAdd = moduleNamesToAdd.filter(isCustomModule);
    const streamNamesToAdd = moduleNamesToAdd.filter(isNotCustomModule);
    const newNotNames = new Set([
        ...neverAllowedSelfServeModules,
        ...remove((view.filter || {}).notNames, ...streamNamesToAdd),
    ]);
    const firstLayout = streamNamesToAdd.reduce<IObserveLayout>(
        (previous, name) => {
            const size = getDefaultSize(name, info);
            const position = findAvailableSpot(previous, size);
            return {
                ...previous,
                [name]: { ...position, ...size },
            };
        },
        viewLayout,
    );

    const finalLayout = customModuleNamesToAdd.reduce<IObserveLayout>(
        (previous, name) => {
            const configuration = customModuleConfiguration[name];
            const size = getDefaultSize(name, info);
            const position = findAvailableSpot(previous, size);

            return {
                ...previous,
                [name]: {
                    ...position,
                    ...size,
                    configuration,
                },
            };
        },
        firstLayout,
    );

    return {
        ...view,
        filter: {
            ...view.filter,
            names: undefined,
            notNames: emptyArrayToUndefined([...newNotNames]),
        },
        layout: finalLayout,
    };
}

export function removeModules(
    view: IView,
    ...moduleNamesToRemove: string[]
): IView {
    const viewLayout = view.layout as IObserveLayout;
    const customModuleNamesToRemove =
        moduleNamesToRemove.filter(isCustomModule);
    const streamNamesToRemove = moduleNamesToRemove.filter(isNotCustomModule);

    const moduleNames = Object.keys(view.layout);
    const layout: IObserveLayout = {};
    for (const name of moduleNames) {
        if (!customModuleNamesToRemove.includes(name)) {
            layout[name] = viewLayout[name];
        }
    }

    const newNotNames = new Set([
        ...((view.filter || {}).notNames || []),
        ...neverAllowedSelfServeModules,
    ]);

    for (const name of streamNamesToRemove) {
        if (!newNotNames.has(name)) {
            delete layout[name];
            newNotNames.add(name);
        }
    }

    const filter: IFilter = {
        ...view.filter,
        names: undefined,
        notNames: emptyArrayToUndefined([...newNotNames]),
    };

    return {
        ...view,
        filter,
        layout,
    };
}

interface IPersistViewStores {
    appStore: AppStore;
    observeStore: ObserveStore;
    viewStore: ViewStore;
}

interface IPersistViewOptions {
    skipNextLayout?: boolean;
    scrollToModule?: string;
}

export async function persistView(
    view: IView,
    stores: IPersistViewStores,
    options: IPersistViewOptions = {},
) {
    const { appStore, observeStore, viewStore } = stores;
    const { skipNextLayout = false, scrollToModule } = options;

    if (skipNextLayout) {
        observeStore.skipNextLayout = true;
    }

    if (scrollToModule) {
        observeStore.autoScrollToModule = scrollToModule;
    }

    try {
        await viewStore.save(view);
    } catch (error: unknown) {
        const responseError = narrowError(error, ResponseError);
        const { statusCode, validationErrors } = responseError;
        switch (statusCode) {
            case 422: {
                const errors = Object.values(validationErrors).join(". ");
                appStore.setSnackbar(errors);
                break;
            }
            case 409: {
                appStore.setSnackbar(
                    viewConflictMessage(
                        responseError,
                        "A layout with this name already exists",
                    ),
                );
                break;
            }
            default: {
                throw error;
            }
        }
    }
}
